import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'
import { useChromeExtension } from './extension'

const CampaignsPage = () => import('./campaign/CampaignsPage.vue')
const AudiencesPage = () => import('./audience/AudiencesPage.vue')
const StandaloneAudiencesPage = () => import('./audience/StandaloneAudiencesPage.vue')
const InviteAcceptPage = () => import('./auth/InviteAcceptPage.vue')
const LoginPage = () => import('./auth/LoginPage.vue')
const OnboardingPage = () => import('./auth/OnboardingPage.vue')
const ManageAccountPage = () => import('./auth/ManageAccountPage.vue')
const CreativesPage = () => import('./creative/CreativesPage.vue')
const MediaOverview = () => import('./media/MediaOverview.vue')
const ChooseTeamPage = () => import('./team/ChooseTeamPage.vue')
const HelperDocumentsPage = () => import('./team/HelperDocumentsPage.vue')
const TeamSettingsPage = () => import('./team/TeamSettingsPage.vue')
const AudienceCreator = () => import('./audience-creator/AudienceCreator.vue')
const ExploreX = () => import('./audience-creator/ExploreX.vue')
const TeamRootPage = () => import('./team/TeamRootPage.vue')
const CommunitiesPage = () => import('./communities/CommunitiesPage.vue')
const ResetE2EPage = () => import('./e2e/ResetE2EPage.vue')

const DeepThoughtNotFoundPage = () => import('./deep-thought/pages/404.vue')
const DeepThoughtLoginPremiumPage = () => import('./deep-thought/pages/LoginPremiumPage.vue')
const DeepThoughtXStatsPage = () => import('./deep-thought/pages/XStatsPage.vue')
const DeepThoughtHomeAuthenticatedPage = () => import('./deep-thought/pages/HomePage.vue')
const DeepThoughtHomePage = () => import('./deep-thought/pages/Paused.vue')
const DashboardAccountFollowerEnrichment = () =>
  import('./deep-thought/components/DashboardAccountFollowerEnrichment.vue')
const DeepThoughtFollowingEnrichments = () => import('./deep-thought/components/FollowingEnrichments.vue')
const DeepThoughtUnfollowedAccounts = () => import('./deep-thought/components/DashboardUnfollowedAccounts.vue')
const DeepThoughtDashboardPage = () => import('./deep-thought/pages/DashboardPage.vue')
const DeepThoughtTalkToXPage = () => import('./deep-thought/pages/TalkToX.vue')
const DeepThoughtAccountsPage = () => import('./deep-thought/components/DashboardAccountInfoAccounts.vue')
const DeepThoughtFollowersPage = () => import('./deep-thought/components/DashboardAccountInfoFollowers.vue')
const DeepThoughtFollowingPage = () => import('./deep-thought/components/DashboardAccountInfoFollowing.vue')
const DeepThoughtListsPage = () => import('./deep-thought/components/DashboardLists.vue')
const DeepThoughtPostPage = () => import('./deep-thought/components/TwitterPostItem.vue')
const DeepThoughtPostsPage = () => import('./deep-thought/components/DashboardPosts.vue')
const DeepThoughtEngagersPage = () => import('./deep-thought/components/DashboardEngagers.vue')
const DeepThoughtCreateSubscriptionSuccessPage = () => import('./deep-thought/pages/CreateSubscriptionSuccess.vue')
const DeepThoughtCreateSubscriptionCancelPage = () => import('./deep-thought/pages/CreateSubscriptionCancel.vue')
const DeepThoughtSwitchAccountPage = () => import('./deep-thought/pages/SwitchAccount.vue')

const ProTargetingHomePage = () => import('./pro-targeting/pages/HomePage.vue')
const ProTargetingSavedListsPage = () => import('./pro-targeting/pages/SavedListsPage.vue')
const ProTargetingCampaignsNewPage = () => import('./pro-targeting/pages/CampaignsNewPage.vue')
const ProTargetingTargetingListsPage = () => import('./pro-targeting/pages/TargetingListsPage.vue')
const ProTargetingCampaignCreatePage = () => import('./pro-targeting/pages/CampaignCreatePage.vue')
const ProTargetingExplorePage = () => import('./pro-targeting/pages/ExplorePage.vue')
const ProTargetingClustersPage = () => import('./pro-targeting/pages/ClustersPage.vue')
const ProTargetingXStatsPage = () => import('./pro-targeting/pages/XStats.vue')
const ProTargetingAudienceBuilderPage = () => import('./pro-targeting/pages/AudienceBuilderPage.vue')
const ProTargetingCreatorsPage = () => import('./pro-targeting/pages/CreatorsPage.vue')
const ProTargetingCampaignPlannerPage = () => import('./pro-targeting/pages/CampaignPlannerPage.vue')
const ProTargetingCompetitorAnalysisPage = () => import('./pro-targeting/pages/CompetitorAnalysisPage.vue')
const ProTargetingSavedAudiencesPage = () => import('./pro-targeting/pages/SavedAudiencesPage.vue')
const ProTargetingMarketingMaterialsPage = () => import('./pro-targeting/pages/MarketingMaterialsPage.vue')
const ProTargetingPartnershipPage = () => import('./pro-targeting/pages/PartnershipPage.vue')
const ProTargetingPage = () => import('./pro-targeting/pages/ProTargetingPage.vue')
const ProTargetingProSolutionsPage = () => import('./pro-targeting/pages/ProSolutionsPage.vue')
const ProTargetingProCommunityPage = () => import('./pro-targeting/pages/ProCommunityPage.vue')
const ProTargetingRequestAudiencePage = () => import('./pro-targeting/components/RequestAudienceContactForm.vue')
const ProTargetingRequestAccessPage = () => import('./pro-targeting/components/RequestAccess.vue')
const ProTargetingRequestDownloadPage = () => import('./pro-targeting/components/RequestDownload.vue')
const ProTargetingRequestConfirmationPage = () => import('./pro-targeting/components/RequestConfirmation.vue')
const ProTargetingRequestVerificationPage = () => import('./pro-targeting/components/RequestVerification.vue')
const ProTargetingJobTitleTargetingPage = () => import('./pro-targeting/components/JobTitleConfirmation.vue')

const { isInExtension } = useChromeExtension()

const SAFE_STALE_ROUTES = ['request-download', 'request-access', 'request-confirmation', 'request-verification']

const dashboardRoutes: RouteRecordRaw[] = [
  { path: '/login/:network?', name: 'login', component: LoginPage, props: true, meta: { layout: 'dashboard-no-nav' } },
  {
    path: '/:teamId/invite-accept',
    name: 'invite-accept',
    component: InviteAcceptPage,
    meta: { layout: 'dashboard-no-nav' },
  },
  {
    path: '/welcome',
    name: 'onboarding',
    component: OnboardingPage,
    props: true,
    meta: { layout: 'dashboard-no-nav' },
  },
  { path: '/choose-team', name: 'choose-team', component: ChooseTeamPage, meta: { layout: 'dashboard-no-nav' } },
  {
    path: '/helper-documents',
    name: 'helper-documents',
    component: HelperDocumentsPage,
    meta: { layout: 'dashboard-no-nav' },
  },

  {
    path: '/audiences',
    name: 'standalone-audiences',
    component: StandaloneAudiencesPage,
    meta: { layout: 'dashboard-no-nav', header: true },
  },

  {
    path: '/:teamId/',
    name: 'team-root',
    component: TeamRootPage,
    props: true,
    meta: { layout: 'dashboard-no-nav' },
    redirect: { name: 'audiences' },
    children: [
      { path: 'account', name: 'manage-account', component: ManageAccountPage, meta: { layout: 'dashboard' } },
      { path: 'team', name: 'team-settings', component: TeamSettingsPage, meta: { layout: 'dashboard' } },
      { path: 'campaigns', name: 'campaigns', component: CampaignsPage, meta: { layout: 'dashboard', header: true } },
      { path: 'creatives', name: 'creatives', component: CreativesPage, meta: { layout: 'dashboard' } },
      { path: 'media', name: 'media', component: MediaOverview, meta: { layout: 'dashboard' } },
      {
        path: 'creator',
        name: 'audience-creator',
        component: AudienceCreator,
        meta: { layout: 'dashboard', header: true },
      },
      { path: 'explore-x', name: 'explore-x', component: ExploreX, meta: { layout: 'dashboard', header: true } },
      { path: 'audiences', name: 'audiences', component: AudiencesPage, meta: { layout: 'dashboard', header: true } },

      // These pages are no longer in the nav menu, but are still accessible via direct link:
      {
        path: 'communities',
        name: 'communities',
        component: CommunitiesPage,
        meta: { layout: 'dashboard', header: true },
      },
    ],
  },

  { path: '/reset-e2e', name: 'reset-e2e', component: ResetE2EPage, meta: { layout: 'none' } },

  { path: '/:pathMatch(.*)*', redirect: 'choose-team' },
]

const proTargetingRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: ProTargetingHomePage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white', tabs: true },
  },
  {
    path: '/saved-lists',
    name: 'saved-lists',
    component: ProTargetingSavedListsPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white' },
  },
  {
    path: '/campaigns/new',
    name: 'campaigns-new',
    component: ProTargetingCampaignsNewPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white' },
  },
  {
    path: '/campaigns/create',
    name: 'campaigns-create',
    component: ProTargetingCampaignCreatePage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white' },
  },
  {
    path: '/targeting-lists/:id?',
    name: 'targeting-lists',
    alias: ['/native-lists/:id?', '/native-list/:id?'],
    component: ProTargetingTargetingListsPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white' },
  },
  {
    path: '/explore/:name?',
    name: 'explore',
    props: true,
    alias: ['/xplore/:name?', '/analyze/:name?'],
    component: ProTargetingExplorePage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/topic-clusters',
    name: 'clusters',
    alias: '/clusters',
    component: ProTargetingClustersPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/xstats/:country?',
    props: true,
    name: 'xstats',
    component: ProTargetingXStatsPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/audience-builder',
    props: true,
    name: 'audience-builder',
    component: ProTargetingAudienceBuilderPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/competitor-analysis',
    props: true,
    name: 'competitor-analysis',
    component: ProTargetingCompetitorAnalysisPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/my-audiences',
    props: true,
    name: 'saved-audiences',
    alias: '/saved-audiences',
    component: ProTargetingSavedAudiencesPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/creators',
    props: true,
    name: 'creators',
    component: ProTargetingCreatorsPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/campaign-planner',
    props: true,
    name: 'campaign-planner',
    component: ProTargetingCampaignPlannerPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white-white', sidebar: true },
  },
  {
    path: '/marketing-materials',
    name: 'marketing-materials',
    alias: '/materials',
    component: ProTargetingMarketingMaterialsPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white', tabs: true },
  },
  {
    path: '/about',
    name: 'about-us',
    component: ProTargetingPartnershipPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white', tabs: true },
  },
  {
    path: '/pro-targeting',
    name: 'pro-targeting',
    component: ProTargetingPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white', tabs: true },
  },
  {
    path: '/pro-solutions',
    name: 'pro-solutions',
    component: ProTargetingProSolutionsPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white', tabs: true },
  },
  {
    path: '/pro-community',
    name: 'pro-community',
    component: ProTargetingProCommunityPage,
    meta: { layout: 'pro-targeting', backgroundColor: 'white', tabs: true },
  },
  {
    path: '/request-audience',
    name: 'request-audience',
    alias: '/request-audiences',
    component: ProTargetingRequestAudiencePage,
    meta: { layout: 'pro-targeting' },
  },
  {
    path: '/request-download/:title?',
    name: 'request-download',
    component: ProTargetingRequestDownloadPage,
    meta: { layout: 'pro-targeting' },
    props: true,
  },
  {
    path: '/request-access',
    name: 'request-access',
    component: ProTargetingRequestAccessPage,
    meta: { layout: 'pro-targeting' },
  },
  {
    path: '/request-confirmation',
    name: 'request-confirmation',
    component: ProTargetingRequestConfirmationPage,
    meta: { layout: 'pro-targeting' },
  },
  {
    path: '/request-verification',
    name: 'request-verification',
    component: ProTargetingRequestVerificationPage,
    meta: { layout: 'pro-targeting' },
  },
  {
    path: '/job-title',
    name: 'job-title',
    component: ProTargetingJobTitleTargetingPage,
    meta: { layout: 'pro-targeting' },
  },

  { path: '/:pathMatch(.*)*', redirect: 'home' },
]

const deepThoughtRoutes: RouteRecordRaw[] = [
  {
    path: '/authenticated',
    name: 'homepage-authenticated',
    alias: '/login/twitter',
    component: DeepThoughtHomeAuthenticatedPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/',
    name: 'homepage',
    alias: '/login/twitter',
    component: DeepThoughtHomePage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/topics',
    name: 'enrichments',
    alias: '/topics',
    component: DashboardAccountFollowerEnrichment,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/following-topics',
    name: 'following-enrichments',
    alias: '/following-topics',
    component: DeepThoughtFollowingEnrichments,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/unfollowed',
    name: 'unfollowed',
    component: DeepThoughtUnfollowedAccounts,
    meta: { layout: 'deep-thought' },
  },
  { path: '/premium', name: 'premium', component: DeepThoughtLoginPremiumPage, meta: { layout: 'deep-thought' } },
  { path: '/premium-suite', name: 'dashboard', component: DeepThoughtDashboardPage, meta: { layout: 'deep-thought' } },
  {
    path: '/premium-suite/talk-to-x',
    name: 'talktox',
    alias: '/talk-to-x',
    component: DeepThoughtTalkToXPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/xstats/:country?',
    props: true,
    name: 'xstats',
    alias: '/xstats/:country?',
    component: DeepThoughtXStatsPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/accounts',
    name: 'accounts',
    component: DeepThoughtAccountsPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/followers',
    name: 'followers',
    component: DeepThoughtFollowersPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/following',
    name: 'following',
    component: DeepThoughtFollowingPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/posts',
    name: 'posts',
    component: DeepThoughtPostsPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/post/:id',
    name: 'post',
    props: true,
    component: DeepThoughtPostPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/engagers',
    name: 'engagers',
    component: DeepThoughtEngagersPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/premium-suite/lists',
    name: 'lists',
    component: DeepThoughtListsPage,
    meta: { layout: 'deep-thought' },
  },
  {
    path: '/create-subscription/success',
    name: 'create-subscription-success',
    component: DeepThoughtCreateSubscriptionSuccessPage,
    meta: { layout: 'none' },
  },
  {
    path: '/create-subscription/cancel',
    name: 'create-subscription-cancel',
    component: DeepThoughtCreateSubscriptionCancelPage,
    meta: { layout: 'none' },
  },
  {
    path: '/switch-account',
    name: 'switch-account',
    component: DeepThoughtSwitchAccountPage,
    meta: { layout: 'none' },
  },

  { path: '/:pathMatch(.*)*', name: '404', component: DeepThoughtNotFoundPage, meta: { layout: 'deep-thought' } },
]

export const router = createRouter({
  history: createWebHistory(),
  routes:
    window.location.href.includes('xprotargeting.com') ||
    (window.location.href.includes('?protargeting=true') && window.location.href.includes('staging'))
      ? proTargetingRoutes
      : window.location.href.includes('deepthought.com') ||
          (window.location.href.includes('?deepthought=true') && window.location.href.includes('staging'))
        ? deepThoughtRoutes
        : dashboardRoutes,
  scrollBehavior(to, _, savedPosition) {
    if (to.hash) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ el: to.hash, behavior: 'smooth' })
        }, 200)
      })
    }

    if (savedPosition) {
      return savedPosition
    }

    return { top: 0 }
  },
})

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') || // Chrome
    error.message.includes('error loading dynamically imported module') || // Firefox
    error.message.includes('Importing a module script failed') // Safari
  ) {
    if (to.name && SAFE_STALE_ROUTES.includes(to.name.toString()) && (!to.meta || !to.meta._retried)) {
      to.meta = { ...to.meta, _retried: true }
      router.push(to)
      return
    }

    const message = isInExtension.value
      ? "A new version of the app is available and a refresh is required to continue. Please refresh this page (control/command + R or via the refresh button) and try again. If that doesn't work, please close and reopen this page and try again."
      : 'A new version of the app is available and a refresh is required to continue. Please refresh this page (control/command + R or via the refresh button) and try again.'
    alert(message)
  }
})
